<template>
  <div class="custom-input">
    <v-menu v-model="open" offset-overflow :close-on-content-click="false" :disabled="disabled || readonly">
      <template v-slot:activator="{ on, attrs }">
        <label v-if="$validate.DataValid(label)" class="input-label">{{ $t(label) }}</label>
        <v-text-field
          outlined
          single-line
          :dense="dense"
          :value="inputDisplayValue"
          :disabled="disabled"
          readonly
          :hide-details="hideDetails"
          type="text"
          :rules="rules"
          :placeholder="$t(placeholder)"
          color="primary accent-4"
          :class="inputClass"
          autocomplete="new-password"
          persistent-placeholder
          v-bind="attrs"
          v-on="on"
          @click:clear="_onClear()"
          :clearable="enableClear"
          :append-icon="displaySearchIcon ? icon.mdiMagnify : null"
          @click="_openSearch()"
          :hint="$t(hint)"
          :persistent-hint="hint !== ''"
          :loading="searching"
        >
          <template v-slot:append>
            <slot name="append-inner"></slot>
          </template>
        </v-text-field>
      </template>

      <v-container fluid style="background: #ffffff;" class="pa-2">
        <v-text-field
          v-model="searchText"
          clearable
          dense
          hide-details
          :rules="searchTextRules"
          :placeholder="$t(searchPlaceholder)"
          @click:clear="_clickSearch('')"
          :append-icon="icon.mdiMagnify"
          @click:append="_clickSearch(searchText)"
          @keydown.enter="_clickSearch(searchText)"
          autofocus
          @input="_inputSearch($event)"
        />
        <v-progress-linear v-if="searching" indeterminate color="primary" />
        <v-list class="mt-2" style="max-height: 50vh; overflow-x: hidden; overflow-y: auto;">
          <template v-if="multiple">
            <v-list-item v-show="!multiOptions || multiOptions.length < 1">
              <v-list-item-title>{{ $t('table.noData') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="(item, index) in multiOptions"
              :key="`option_${index}`"
              :value="item.value"
              @click.prevent="_onMultiOptionClick(item)"
            >
              <v-list-item-action>
                <v-checkbox :input-value="item.active"></v-checkbox>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-else>
            <v-list-item v-show="!options || options.length < 1">
              <v-list-item-title>{{ $t('table.noData') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-for="(item, index) in options" :key="`option_${index}`" @click="_onSingleOptionClick(item)">
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </v-container>
    </v-menu>
  </div>
</template>

<script>
import { mdiMagnify } from '@mdi/js'
export default {
  name: 'FormSearchSelect',
  props: {
    fieldValue: {
      type: String | Number | Array,
      required: true,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
      required: false,
    },
    searching: {
      type: Boolean,
      default: false,
      required: false,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: true,
    },
    customInputClass: {
      type: String,
      required: false,
      default: '',
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchTextRules: {
      type: Array,
      default: () => [],
    },
    searchPlaceholder: {
      type: String,
      required: false,
      default: 'table.search',
    },
    enableClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    displaySearchIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: String,
      required: false,
      default: '',
    },
  },
  watch: {
    fieldValue: {
      handler(val) {
        if (!this.$validate.DataValid(val) || val === -1 || (this.multiple && val.length < 1)) {
          this.inputDisplayValue = ''
          this.selectedValues = []
        } else {
          if (this.multiple) {
            this.selectedValues = JSON.parse(JSON.stringify(val))
            this._updateSelectedOptions()
            this.inputDisplayValue = this.selectedOptions.map(el => el.text).join(', ')
          } else {
            const found = this.options.find(el => el.value == val)
            if (found) {
              this.inputDisplayValue = found.text
            }
          }
        }
      },
      deep: true,
      immediate: true,
    },
    options: {
      handler(val) {
        if (this.multiple) {
          for (let i = 0; i < val.length; i++) {
            if (this.selectedValues.includes(val[i].value)) {
              this.$set(val[i], 'active', true)
            } else {
              this.$set(val[i], 'active', false)
            }
          }
          this._updateSelectedOptions()
          this.inputDisplayValue = this.selectedOptions.map(el => el.text).join(', ')
        } else {
          const found = val.find(el => el.value === this.fieldValue)
          if (found) {
            this.inputDisplayValue = found.text
          }
        }
      },
    },
  },
  computed: {
    rules() {
      return [v => (this.required ? this.$validate.required(v) || this.$t('message.formRequired') : true)]
    },
    inputClass() {
      let classObj = {
        'pointer-none': this.$store.getters.isLoading || this.disabled || this.readonly,
      }
      if (this.$validate.DataValid(this.customInputClass)) {
        const arr = this.customInputClass.split(' ')
        arr.forEach(name => {
          if (this.$validate.DataValid(name.trim())) {
            classObj[name.trim()] = true
          }
        })
      }

      return classObj
    },
    multiOptions() {
      if (this.searchText !== '' && this.$validate.DataValid(this.options, true)) {
        return this.options
      }

      return this.selectedOptions
    },
  },
  data: () => ({
    open: false,
    icon: {
      mdiMagnify,
    },
    searchText: '',
    inputDisplayValue: '',

    selectedValues: [],
    selectedOptions: [],
  }),
  methods: {
    clearSearchText() {
      this.searchText = ''
    },
    _onClear() {
      this.selectedOptions = []
      if (this.multiple) {
        this.$emit('update:fieldValue', [])
        this.$emit('clear-value', [])
        this.$emit('changed', [])
      } else {
        this.$emit('update:fieldValue', '')
        this.$emit('clear-value', '')
        this.$emit('changed', '')
      }
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },

    _openSearch() {
      this.open = true
      if (this.multiple) {
        this.searchText = ''
        this.$emit('click-search', '')
      }
    },
    _inputSearch(value) {
      this.$emit('input-search', value)
    },
    _clickSearch(value) {
      this.$emit('click-search', value)
    },

    _onMultiOptionClick(item) {
      item.active = !item.active

      const list = JSON.parse(JSON.stringify(this.selectedValues))
      if (item.active && !list.includes(item.value)) {
        list.push(item.value)
      } else if (!item.active) {
        for (let i = list.length - 1; i >= 0; i--) {
          if (item.value === list[i]) {
            list.splice(i, 1)
          }
        }
      }
      this.selectedValues = list
      this._updateSelectedOptions()

      this.$emit('update:fieldValue', this.selectedValues)
      this.$emit('onselect', this.selectedValues)
      this.$emit('changed', this.selectedValues)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },
    _updateSelectedOptions() {
      if (this.$validate.DataValid(this.options, true)) {
        for (let i = 0; i < this.selectedValues.length; i++) {
          const foundSelected = this.selectedOptions.find(el => el.value === this.selectedValues[i])
          if (!foundSelected) {
            const found = this.options.find(el => el.value === this.selectedValues[i])
            if (found) {
              this.selectedOptions.push(JSON.parse(JSON.stringify(found)))
            }
          }
        }
      }

      for (let i = this.selectedOptions.length - 1; i >= 0; i--) {
        if (this.selectedValues.includes(this.selectedOptions[i].value)) {
          this.$set(this.selectedOptions[i], 'active', true)
        } else {
          this.selectedOptions.splice(i, 1)
        }
      }

      for (let i = 0; i < this.options.length; i++) {
        if (this.selectedValues.includes(this.options[i].value)) {
          this.$set(this.options[i], 'active', true)
        } else {
          this.$set(this.options[i], 'active', false)
        }
      }
    },

    _onSingleOptionClick(item) {
      this.$emit('update:fieldValue', item.value)
      this.$emit('onselect', item.value)
      this.$emit('changed', item.value)
      this.open = false
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },
  },
}
</script>
